import { useEffect } from 'react';

import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import useAsyncListActions from '@/hooks/useList/useAsyncListActions';

import { UseAsyncListSelectAllManagerReturnType } from '../types';

type UseAsyncListSelectAllManagerProps<T> = {
  selectedListItemIds: string[];
  excludeSelectedListItemIds?: string[];
  disabled?: boolean;
  filterManager: ReturnType<typeof useFilterManagerWithState>;
  useData: () => {
    listItems: T[];
    isFetching: boolean;
    totalCount?: number;
    fetchMore?: () => void;
  };
};

export default function useAsyncListSelectAllManager<T>(
  props: UseAsyncListSelectAllManagerProps<T>,
): UseAsyncListSelectAllManagerReturnType<T> {
  const { selectedListItemIds, disabled, useData, excludeSelectedListItemIds, filterManager } =
    props;
  const asyncList = useAsyncListActions({
    includeIds: selectedListItemIds,
    excludeIds: excludeSelectedListItemIds ?? [],
    isSelectAll: false,
  });
  const { state, methods } = asyncList;
  const { onSelectAllChange, getSelectedCount, onReset } = methods;
  const { isSelectAll, excludeIds, includeIds } = state;
  const {
    state: { filter, search },
  } = filterManager;

  const { listItems, isFetching, fetchMore, totalCount } = useData();
  const selectedCount = getSelectedCount(totalCount);

  /**
   * https://www.notion.so/lyearn/Select-All-in-Assignment-3b8bf7b2e7d84ef3a25263dc47b7b0e5?pvs=4#ea044628dbba4df1acaaee5d90f3b266
   * In the selection modal,
   * if the user has selected via select all,
   * and they remove the search/filter,
   * the selection would be wiped off.
   */
  useEffect(() => {
    if (disabled) {
      return;
    }
    if (isSelectAll) {
      onReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search, disabled]);

  useEffect(() => {
    if (!isSelectAll && totalCount && totalCount === selectedCount && excludeIds?.length === 0) {
      onSelectAllChange();
    } else if (selectedCount === 0 && includeIds?.length === 0) {
      onReset();
    }
  }, [
    excludeIds?.length,
    includeIds?.length,
    isSelectAll,
    onReset,
    onSelectAllChange,
    selectedCount,
    totalCount,
  ]);

  return {
    isFetching,
    data: listItems,
    fetchMore,
    selectedCount,
    asyncList,
    totalCount,
  };
}
